import { useEffect, useState } from "react";
import { Button, Col, Row } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import AlertModal from "../../../../components/alertModal/alertModal";
import CustomBreadcrumb from "../../../../components/customBreadcrumb/CustomBreadcrumb";
import MainLoader from "../../../../components/loading/MainLoader";
import { ISuccessState } from "../../../../interfaces/successState";
import { IRootState } from "../../../../store/configureStore";
import { TableColumn } from "react-data-table-component";
import DataTableBase from "../../../../components/dataTableBase/DataTableBase";
import SelectOption from "../../../../components/selectOption/SelectOption";
import {
  allocateCircuitGroup,
  deAllocateCircuitGroup,
  getCustomerCircuitGroups,
  getUserCircuitGroups,
} from "../../../../services/settings";
import { ICustomerCircuitGroup } from "../../../../interfaces/customerCircuitGroup";
import TooltipPopup from "../../../../components/tooltipPopup/TooltipPopup";
import { AiOutlineDelete } from "react-icons/ai";
import styles from "./AssignCircuitGroup.module.scss";
import SearchBar from "../../../../components/searchBar/SearchBar";

const AssignCircuitGroup = () => {
  const { userId, name } = useParams();
  const numericUserId = Number(userId);
  const [isSuccess, setIsSuccess] = useState<ISuccessState | undefined>();
  const { user, loadingUserById } = useSelector(
    ({ externalUser, privileges }: IRootState) => ({
      user: privileges.dataBundleUser,
      loadingUserById: externalUser.loadingDataObject,
    })
  );
  const [groups, setGroups] = useState<ICustomerCircuitGroup[]>([]);
  const [userGroups, setUserGroups] = useState<ICustomerCircuitGroup[]>([]);
  const [filteredUserGroups, setFilteredUserGroups] = useState<
    ICustomerCircuitGroup[]
  >([]);
  const [search, setSearch] = useState("");
  const [selectedGroup, setSelectedGroup] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [deAllocatingId, setDeallocatingId] = useState<string | undefined>();

  const getUserCircuitGroupsHandler = () => {
    if (userId && user) {
      setIsLoading(true);
      getUserCircuitGroups(userId)
        .then((r) => {
          setUserGroups(r);
          setFilteredUserGroups(r);
          setIsLoading(false);
        })
        .catch(() => {
          setIsLoading(false);
        });
    }
  };

  useEffect(() => {
    if (userId && user) {
      setIsLoading(true);
      getCustomerCircuitGroups(user.customer.id)
        .then((r) => {
          setGroups(r);
          setIsLoading(false);
        })
        .catch(() => {
          setIsLoading(false);
        });
      getUserCircuitGroupsHandler();
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const result = userGroups.filter((groups) => {
      return groups.circuitId
        .toLocaleLowerCase()
        .match(search.toLocaleLowerCase());
    });
    setFilteredUserGroups(result);
  }, [search, userGroups]);

  const deAllocateCircuitGroupHandler = () => {
    if (userId && user && deAllocatingId) {
      setIsLoading(true);
      deAllocateCircuitGroup(userId, deAllocatingId).then(() => {
        setDeallocatingId(undefined);
        getUserCircuitGroupsHandler();
        setIsLoading(false);
      });
    }
  };

  const handleSubmit = () => {
    if (numericUserId && selectedGroup) {
      allocateCircuitGroup(numericUserId, selectedGroup).then((r) => {
        if (r.dataBundle.alreadyAssignedCircuitList) {
          setIsSuccess({
            isSuccess: false,
            message: "This circuit is already assigned",
          });
        } else {
          getUserCircuitGroupsHandler();
          setIsSuccess({
            isSuccess: true,
            message: "Successfully allocated a new circuit",
          });
        }
      });
    }
  };

  const columns: TableColumn<ICustomerCircuitGroup>[] = [
    {
      name: "Circuit ID",
      selector: (row: ICustomerCircuitGroup) => row?.circuitId,
    },
    {
      name: "Group Nick Name",
      selector: (row: ICustomerCircuitGroup) => row?.groupNickName,
    },
    {
      name: "Package Name",
      selector: (row: ICustomerCircuitGroup) => row?.packageName,
    },
    {
      name: "Package Type",
      selector: (row: ICustomerCircuitGroup) => row?.packageType,
    },
    {
      name: "Actions",
      cell: (row: ICustomerCircuitGroup) => (
        <Col className="text-center">
          <TooltipPopup tooltip="Delete">
            <AiOutlineDelete
              className={styles.actionIcons}
              color="#FF6464"
              onClick={() => setDeallocatingId(row?.circuitId)}
            />
          </TooltipPopup>
        </Col>
      ),
    },
  ];
  const paginationComponentOptions = {
    selectAllRowsItem: true,
  };
  return (
    <div className="page-container">
      <MainLoader show={loadingUserById || isLoading} />
      <CustomBreadcrumb
        items={[
          { path: "/", title: "Home" },
          { path: "/settings/users", title: "Manage Users" },
          { path: "/settings/users/", title: "Assign Circuit" },
        ]}
      />
      <label className="mt-2">
        Assign Circuits to <b>{name}</b>
      </label>
      <Row className="mb-3 mt-4">
        <form>
          <Row className=" mt-2">
            <Col>
              <SelectOption
                placeholder="Select circuit ID"
                onChange={(val) => {
                  setSelectedGroup(val.value);
                }}
                required
                options={groups?.map((item: ICustomerCircuitGroup) => ({
                  label: item.circuitId,
                  value: item.circuitId,
                }))}
              />
            </Col>
            <Col>
              <Button className="w-50" onClick={handleSubmit} variant="success">
                Submit
              </Button>
            </Col>
          </Row>
        </form>
      </Row>

      {/*<label className='mt-3'>Customer Circuit</label>*/}
      <Row className="justify-content-end mt-5">
        <Col className="col-md-5">
          <SearchBar placeholder="Circuit ID" onChange={setSearch} />
        </Col>
      </Row>
      <Row className="justify-content-center">
        <Col>
          <DataTableBase
            pagination
            paginationComponentOptions={paginationComponentOptions}
            className="mt-3 justify-content-center"
            columns={columns}
            data={filteredUserGroups ? filteredUserGroups : []}
            progressPending={false}
          />
        </Col>
      </Row>

      <AlertModal
        show={!!isSuccess}
        variant={isSuccess?.isSuccess ? "success" : "error"}
        buttons={[
          {
            text: "OK",
            onClick: () => setIsSuccess(undefined),
            variant: "success",
          },
        ]}
      >
        {isSuccess?.message}
      </AlertModal>

      <AlertModal
        show={!!deAllocatingId}
        buttons={[
          {
            variant: "danger",
            text: "OK",
            onClick: deAllocateCircuitGroupHandler,
          },
          {
            variant: "success",
            text: "Cancel",
            onClick: () => {
              setDeallocatingId(undefined);
            },
          },
        ]}
        variant="error"
      >
        Are you sure you want to de-allocate this circuit group?
      </AlertModal>
    </div>
  );
};

export default AssignCircuitGroup;
